import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TechnologyShowcase = () => {
  const [hoveredTech, setHoveredTech] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const technologies = [
    {
      id: 'react',
      name: 'React 19',
      icon: 'Atom',
      color: 'from-cyan-400 to-blue-500',
      description: 'The latest evolution of the library, featuring the compiler, server actions, and improved concurrent rendering.',
      capabilities: ['React Compiler', 'Server Components', 'Actions', 'Asset Loading']
    },
    {
      id: 'nextjs',
      name: 'Next.js 14',
      icon: 'Layout',
      color: 'from-gray-800 to-gray-600',
      description: 'Full-stack React framework with App Router, Server Actions, and edge runtime optimization.',
      capabilities: ['App Router', 'Server Actions', 'Edge Runtime', 'SEO Optimized']
    },
    {
      id: 'python',
      name: 'Python',
      icon: 'Terminal',
      color: 'from-blue-500 to-yellow-500',
      description: 'Powerful language for AI/ML integration, data analysis, and robust backend microservices.',
      capabilities: ['AI/ML Integration', 'Data Science', 'Fast API', 'Automation']
    },
    {
      id: 'react-native',
      name: 'React Native',
      icon: 'Smartphone',
      color: 'from-blue-400 to-cyan-400',
      description: 'Native mobile apps for iOS and Android using a single codebase with native performance.',
      capabilities: ['Cross-Platform', 'Native Modules', 'Hot Reload', 'Over-the-Air Updates']
    },
    {
      id: 'nodejs',
      name: 'Node.js',
      icon: 'Server',
      color: 'from-green-500 to-emerald-500',
      description: 'Scalable server-side runtime built on Chrome\'s V8 JavaScript engine for high-performance APIs.',
      capabilities: ['Event Driven', 'Non-blocking I/O', 'Microservices', 'Real-time Socket']
    },
    {
      id: 'mongodb',
      name: 'MongoDB',
      icon: 'Database',
      color: 'from-green-500 to-emerald-600',
      description: 'NoSQL database for flexible, scalable data storage with JSON-like documents and high performance.',
      capabilities: ['NoSQL', 'Scalability', 'High Availability', 'Flexible Schema']
    },
    {
      id: 'postgresql',
      name: 'PostgreSQL',
      icon: 'Database',
      color: 'from-blue-600 to-indigo-700',
      description: 'Advanced open-source relational database with robust SQL support, reliability, and data integrity.',
      capabilities: ['Relational', 'ACID Compliance', 'JSON Support', 'Extensions']
    },
    {
      id: 'typescript',
      name: 'TypeScript',
      icon: 'Code2',
      color: 'from-blue-600 to-indigo-600',
      description: 'Type-safe development with advanced IntelliSense, refactoring tools, and error prevention.',
      capabilities: ['Static Typing', 'IntelliSense', 'Refactoring', 'Error Prevention']
    },
    {
      id: 'aws',
      name: 'AWS',
      icon: 'Cloud',
      color: 'from-orange-500 to-yellow-600',
      description: 'Comprehensive cloud computing platform for scalable, reliable, and secure application hosting and infrastructure.',
      capabilities: ['EC2', 'S3', 'Lambda', 'CloudFront']
    },
    {
      id: 'mysql',
      name: 'MySQL',
      icon: 'Database',
      color: 'from-blue-500 to-cyan-600',
      description: 'Open-source relational database management system known for its reliability, performance, and ease of use.',
      capabilities: ['Relational', 'ACID Compliance', 'Replication', 'High Performance']
    },
    {
      id: 'nestjs',
      name: 'Nest.js',
      icon: 'Layers',
      color: 'from-red-500 to-pink-600',
      description: 'Progressive Node.js framework for building efficient, scalable, and enterprise-grade server-side applications.',
      capabilities: ['Modular Architecture', 'Dependency Injection', 'TypeScript', 'Microservices']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            <span className="text-gradient">Technology Mastery</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Explore our cutting-edge technology stack through interactive 3D cards.
            Each technology is carefully selected for maximum performance and developer experience.
          </p>
        </motion.div>

        {/* Interactive Technology Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {technologies.map((tech, index) => (
            <motion.div
              key={tech.id}
              variants={cardVariants}
              className="group relative"
              onMouseEnter={() => setHoveredTech(tech.id)}
              onMouseLeave={() => setHoveredTech(null)}
              whileHover={{
                scale: 1.05,
                rotateY: 5,
                z: 50
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px'
              }}
            >
              {/* Main Card */}
              <div className="relative bg-card border border-border rounded-2xl p-6 shadow-brand hover:shadow-brand-lg transition-all duration-500 overflow-hidden">
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>

                {/* Card Content */}
                <div className="relative z-10">
                  {/* Tech Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${tech.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={tech.icon} size={28} color="white" />
                  </div>

                  {/* Tech Name */}
                  <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                    {tech.name}
                  </h3>

                  {/* Description */}
                  <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                    {tech.description}
                  </p>

                  {/* Capabilities */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Key Capabilities:</h4>
                    <div className="flex flex-wrap gap-2">
                      {tech.capabilities.map((capability, capIndex) => (
                        <span
                          key={capIndex}
                          className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md border"
                        >
                          {capability}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  initial={false}
                  animate={hoveredTech === tech.id ? { opacity: 1 } : { opacity: 0 }}
                />

                {/* 3D Rotation Indicator */}
                <motion.div
                  className="absolute top-4 right-4 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={hoveredTech === tech.id ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Icon name="RotateCw" size={12} color="var(--color-primary)" />
                </motion.div>
              </div>

              {/* Floating Elements */}
              {hoveredTech === tech.id && (
                <motion.div
                  className="absolute -top-2 -right-2 w-4 h-4 bg-primary rounded-full"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologyShowcase;